import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import type { ComponentField, ComponentFieldOption, ComponentOptionLists } from '@vagusx/clients';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/custom/multi-select';
import { factLabel } from './graph';

/**
 * One editor per kind of field a component can describe. Which fields a step
 * has, their labels and help, their choices — all of it comes from the
 * component catalog, so a component added to the engine reaches the builder
 * without a change here. What IS here is one editor per KIND, and the kinds
 * are a closed list the catalog's own schema enforces.
 *
 * Lines a step speaks (`locale-text` stored as a slot, and `line-list`) are
 * not edited here: their words live beside the config, so `StepSettings`
 * renders them with `LineEditor`.
 */

/**
 * The custom editors this builder has. The catalog's vocabulary lists every
 * one a component may name; `field-editors.test.ts` fails when that list gains
 * one this does not have, so a new editor cannot reach authors as a blank.
 */
export const HANDLED_CUSTOM_EDITORS = ['facts', 'text-list', 'line-list'] as const;

export interface FieldEditorProps {
  field: ComponentField;
  value: unknown;
  onCommit: (value: unknown) => void;
  options: ComponentOptionLists;
  /** Facts a gate field inside this step may name. */
  facts: readonly string[];
  disabled?: boolean;
}

function choices(field: ComponentField, lists: ComponentOptionLists): ComponentFieldOption[] {
  if (field.kind !== 'select' && field.kind !== 'multi') return [];
  if (field.options) return field.options;
  return field.source ? (lists[field.source] ?? []) : [];
}

const asString = (v: unknown): string => (typeof v === 'string' ? v : '');
const asStrings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

/** A text box that saves when it loses focus, not on every keystroke. */
function CommitInput({
  value,
  onCommit,
  multiline,
  type = 'text',
  disabled,
  placeholder,
}: {
  value: string;
  onCommit: (value: string) => void;
  multiline?: boolean;
  type?: 'text' | 'number';
  disabled?: boolean;
  placeholder?: string;
}): React.ReactElement {
  const [local, setLocal] = useState(value);
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    setLocal(value);
  }
  const commit = (): void => {
    if (local !== value) onCommit(local);
  };
  return multiline ? (
    <Textarea
      value={local}
      rows={3}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={commit}
    />
  ) : (
    <Input
      value={local}
      type={type}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
      }}
    />
  );
}

export function FieldEditor({
  field,
  value,
  onCommit,
  options,
  facts,
  disabled,
}: FieldEditorProps): React.ReactElement {
  switch (field.kind) {
    case 'text':
    case 'textarea':
      return (
        <CommitInput
          value={asString(value)}
          multiline={field.kind === 'textarea'}
          disabled={disabled}
          onCommit={(v) => onCommit(v.trim() === '' ? undefined : v)}
        />
      );
    case 'number':
    case 'duration':
      return (
        <CommitInput
          type="number"
          value={typeof value === 'number' ? String(value) : ''}
          disabled={disabled}
          onCommit={(v) => onCommit(v.trim() === '' ? undefined : Number(v))}
        />
      );
    case 'boolean':
      return (
        <Switch checked={value === true} disabled={disabled} onCheckedChange={(v) => onCommit(v)} />
      );
    case 'select': {
      const opts = choices(field, options);
      return (
        <Select value={asString(value)} disabled={disabled} onValueChange={(v) => onCommit(v)}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Choose…" />
          </SelectTrigger>
          <SelectContent>
            {opts.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    case 'multi':
      return (
        <MultiSelect
          options={choices(field, options)}
          selected={asStrings(value)}
          disabled={disabled}
          onChange={(v) => onCommit(v)}
        />
      );
    case 'runtime-key': {
      const selected = field.multiple ? asStrings(value) : asString(value) ? [asString(value)] : [];
      const opts = [...new Set([...facts, ...selected])].map((f) => ({
        value: f,
        label: factLabel(f),
        ...(facts.includes(f) ? {} : { description: 'Nothing before this step records this' }),
      }));
      return (
        <MultiSelect
          options={opts}
          selected={selected}
          disabled={disabled}
          placeholder="No condition"
          onChange={(v) =>
            onCommit(field.multiple ? (v.length ? v : undefined) : (v[v.length - 1] ?? undefined))
          }
        />
      );
    }
    case 'field-list':
      return (
        <FieldListEditor
          field={field}
          value={value}
          onCommit={onCommit}
          options={options}
          facts={facts}
          disabled={disabled}
        />
      );
    case 'custom':
      if (field.editor === 'text-list') {
        return <TextListEditor value={asStrings(value)} onCommit={onCommit} disabled={disabled} />;
      }
      if (field.editor === 'facts') {
        return <FactsEditor value={value} onCommit={onCommit} disabled={disabled} />;
      }
      return <Unsupported />;
    case 'locale-text':
      return <Unsupported />;
  }
}

function Unsupported(): React.ReactElement {
  return <p className="text-xs text-muted-foreground">Edited by VagusX in the definition.</p>;
}

/** Short free-text values: topics, the facts an intent sets. */
function TextListEditor({
  value,
  onCommit,
  disabled,
}: {
  value: string[];
  onCommit: (value: unknown) => void;
  disabled?: boolean;
}): React.ReactElement {
  const [draft, setDraft] = useState('');
  const add = (): void => {
    const next = draft.trim();
    if (!next || value.includes(next)) return;
    onCommit([...value, next]);
    setDraft('');
  };
  return (
    <div className="w-full space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {value.map((item) => (
          <Badge key={item} variant="secondary" className="gap-1">
            {item}
            <button
              type="button"
              aria-label={`Remove ${item}`}
              disabled={disabled}
              onClick={() => onCommit(value.filter((v) => v !== item))}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={draft}
          disabled={disabled}
          placeholder="Add…"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button variant="outline" size="sm" disabled={disabled || !draft.trim()} onClick={add}>
          Add
        </Button>
      </div>
    </div>
  );
}

/** Facts a step records on one of its exits: a name and whether it is set. */
function FactsEditor({
  value,
  onCommit,
  disabled,
}: {
  value: unknown;
  onCommit: (value: unknown) => void;
  disabled?: boolean;
}): React.ReactElement {
  const facts =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  const [draft, setDraft] = useState('');
  const set = (next: Record<string, unknown>): void =>
    onCommit(Object.keys(next).length ? next : undefined);
  return (
    <div className="w-full space-y-2">
      {Object.entries(facts).map(([key, on]) => (
        <div key={key} className="flex items-center gap-2">
          <span className="flex-1 truncate font-mono text-xs">{key}</span>
          <Switch
            checked={on === true}
            disabled={disabled}
            onCheckedChange={(v) => set({ ...facts, [key]: v })}
          />
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Remove ${key}`}
            disabled={disabled}
            onClick={() => {
              const next = { ...facts };
              delete next[key];
              set(next);
            }}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <div className="flex gap-2">
        <Input
          value={draft}
          disabled={disabled}
          placeholder="Fact name"
          onChange={(e) => setDraft(e.target.value)}
        />
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || !draft.trim()}
          onClick={() => {
            set({ ...facts, [draft.trim()]: true });
            setDraft('');
          }}
        >
          Add
        </Button>
      </div>
    </div>
  );
}

/** Rows of a fixed shape: the intents a caller may have, the details to confirm. */
function FieldListEditor({
  field,
  value,
  onCommit,
  options,
  facts,
  disabled,
}: FieldEditorProps): React.ReactElement {
  if (field.kind !== 'field-list') return <Unsupported />;
  const rows = Array.isArray(value)
    ? value.filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
    : [];
  const update = (i: number, path: string, v: unknown): void => {
    const next = rows.map((r, j) => {
      if (j !== i) return r;
      const row = { ...r };
      if (v === undefined) delete row[path];
      else row[path] = v;
      return row;
    });
    onCommit(next);
  };
  return (
    <div className="w-full space-y-3">
      {rows.map((row, i) => (
        <div key={i} className="space-y-2 rounded-md border p-3">
          {field.item.map((sub) => (
            <div key={sub.path} className="space-y-1">
              <div className="text-xs text-muted-foreground">{sub.label}</div>
              <FieldEditor
                field={sub}
                value={row[sub.path]}
                options={options}
                facts={facts}
                disabled={disabled}
                onCommit={(v) => update(i, sub.path, v)}
              />
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => onCommit(rows.filter((_, j) => j !== i))}
          >
            Remove
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => onCommit([...rows, {}])}
      >
        <Plus className="mr-2 h-3.5 w-3.5" />
        Add {field.label.toLowerCase()}
      </Button>
    </div>
  );
}
